import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'Components/Modal';
import { PlanLink } from 'Features/Plans/PlanLink';
import { FloatingBottomFooter } from 'Components/FloatingBottomFooter/FloatingBottomFooter';
import { SubmitButton } from 'Components/Fields/Buttons';
import {
  getPlanButtonText,
  getPlanDisplayPrice,
  getPlanSelectFrequencyLabel,
  getSimplifiedPlanDisplayName,
} from 'Utils/Plans';
import { HTMLParser } from 'Utils/Parser';
import { classCombine } from 'Utils/Utils';
import { getSetDefaultPlanIndex } from 'Utils/Analytics/Amplitude';
import './PlanSelection.css';

const getDefaultHighlightedPlan = (selectedPlan, plans) => {
  return selectedPlan || plans[getSetDefaultPlanIndex(plans.length)];
};

export const PlanSelection = ({ plans, selectedPlan, selectPlan }) => {
  const [highlightedPlan, setHighlightedPlan] = useState(() => getDefaultHighlightedPlan(selectedPlan, plans));
  const promoCode = useSelector((s) => s.promoCode);
  const promoMsg = promoCode.hasApplied && promoCode.promoDescription;

  /*
    This is necessary because static page may initially render with a highlighted plan
    But due to an Amplitude experiment or other factors, JS page will have a different
    highlighted plan. This useEffect allows for a rerender with the proper highlighted
    plan from JS site.
   */
  useEffect(() => {
    if (!selectedPlan) {
      setHighlightedPlan(getDefaultHighlightedPlan(selectedPlan, plans));
    }
  }, [selectedPlan, plans]);

  //TODO: will need to update the first arg below when support existing subscribers
  const submitButtonText = getPlanButtonText(false, highlightedPlan);

  return (
    <div className="planSelection">
      {plans.map((plan) => {
        const isSelected = highlightedPlan.planId === plan.planId;
        const {
          promoFeature,
          mainFeature,
          strikethroughPrice,
          purchaseFunnelHardcodeName,
          planSelectionHardcodeMobileName,
          priceHardcode,
          frequencyLabelHardcode,
        } = plan.config;
        const promoText = promoCode.plan === plan.planId ? promoMsg || promoFeature : promoFeature;

        return (
          <button
            className={classCombine('planSelectButton', isSelected ? 'isSelected' : null)}
            key={plan.planId}
            onClick={() => setHighlightedPlan(plan)}
          >
            <div className="planTopRow">
              <div className="planTopLeft">
                <img
                  className="planSelectRadio"
                  alt={isSelected ? 'Selected' : 'Not Selected'}
                  src={`/account/static/images/${isSelected ? '' : 'un'}selected-radio.svg`}
                />
                <div className={classCombine('planName', planSelectionHardcodeMobileName && 'nameHiddenMobile')}>
                  {purchaseFunnelHardcodeName || getSimplifiedPlanDisplayName(plan)}
                </div>
                {planSelectionHardcodeMobileName && (
                  <div className="planName nameOnlyMobile">{planSelectionHardcodeMobileName}</div>
                )}
              </div>
              <div className="planTopRight">
                {strikethroughPrice && <div className="planStrikethrough">{strikethroughPrice}</div>}
                <div className="planPrice">{priceHardcode || getPlanDisplayPrice(plan.planPrice)}</div>
                <div className="planFreqLabel">{frequencyLabelHardcode || getPlanSelectFrequencyLabel(plan)}</div>
              </div>
            </div>
            {/* TODO: don't show promoFeature for existing subs/changeplans once support it */}
            {promoText && (
              <div className="planFeature">
                <img className="featureImage" alt="Check Mark" src="/account/static/images/red-check.svg" />
                <div className="featureText">{promoText}</div>
              </div>
            )}
            {mainFeature && (
              <div className="planFeature">
                <img className="featureImage" alt="Check Mark" src="/account/static/images/red-check.svg" />
                <div className="featureText">{HTMLParser(mainFeature)}</div>
              </div>
            )}
          </button>
        );
      })}
      {/* in modal, select plan with selectPlan func. in signup plans page, select plan with a link */}
      {selectPlan ? (
        <SubmitButton
          modernLayout
          className="planSelectionSubmitButton"
          text={submitButtonText}
          onClick={() => selectPlan(highlightedPlan.planId)}
        />
      ) : (
        <>
          <PlanLink modernStyle className="planSelectionSubmitLink hideMobile" plan={highlightedPlan} />
          <FloatingBottomFooter
            mobileOnly
            component={<PlanLink modernStyle className="planSelectionMobileSubmit" plan={highlightedPlan} />}
          />
        </>
      )}
    </div>
  );
};

export const PlanSelectionModal = ({ show, onClose, plans, selectedPlan, selectPlan }) => {
  return (
    <Modal modernLayout isShowing={show} onClose={onClose}>
      <div>
        <div className="planModalTitle">Change Plan</div>
        {selectedPlan && <PlanSelection plans={plans} selectedPlan={selectedPlan} selectPlan={selectPlan} />}
      </div>
    </Modal>
  );
};
